import { useEffect, useRef, useState } from "react";

import { useTheme } from "next-themes";

import { useGeogebraApplet } from "@/components/graph/useGeogebraApplet";
import { useGeogebraCommandRunner } from "@/components/graph/useGeogebraCommandRunner";
import { buildExpression, getXAxisBounds, useAxisVariables } from "@/components/graph/utils";

import type { GraphOptions } from "@/components/graph/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";

export function useGraphPreview<T extends HTMLElement = HTMLDivElement>(
  expressions: NodeModel[][],
  options: Partial<GraphOptions> = {},
  doc?: DocumentModel,
) {
  const ref = useRef<T>(null);
  const { axisVariables } = useAxisVariables(expressions);
  const xAxisBounds = doc ? getXAxisBounds(doc, axisVariables.x) : undefined;
  const { expression, rangedValueExpression, rangedValues } = buildExpression(
    expressions,
    axisVariables,
    xAxisBounds,
  );

  // always render after first render
  // (otherwise it can lead to graph autofocus)
  const [enabled, setEnabled] = useState(expression.length > 0);

  useEffect(() => {
    if (expression.length > 0) setEnabled(true);
  }, [expression]);

  const { theme, systemTheme } = useTheme();
  const currentTheme = (theme === "system" ? systemTheme : (theme as "dark" | "light")) ?? "light";

  const { api } = useGeogebraApplet(ref, enabled, options, currentTheme);
  const commandRunner = useGeogebraCommandRunner({
    api,
    expression,
    rangedValueExpression,
  });

  return {
    ref,
    api,
    expression,
    rangedValues,
    commandRunner,
    axisVariables,
  };
}
